import mixpanel from "mixpanel-browser";
import React, {
  createContext,
  useContext,
  type ReactNode,
  useEffect,
} from "react";
import { useLocation } from "react-router-dom";

import appConfig from "@/config/app-config";
import { useGdprTracking, useTvSource } from "@/hooks";
import sourceCode from "@/utils/code";
import { isEmbedPath } from "@/utils/embed-path";
import { initMixpanel, isMixpanelInitialized } from "@/utils/mixpanel";

import { useAuth } from "./auth/useAuth";

interface AnalyticsProps {
  track: typeof mixpanel.track;
  optInTracking(): void;
  optOutTracking(): void;
}

/**
 * Usage
 *
 * const { track } = useAnalytics();
 * useEffect(() => { track('Feed') }, []);
 */

const AnalyticsContext = createContext({} as AnalyticsProps);
const useAnalytics = () => useContext(AnalyticsContext);

const AnalyticsProvider = ({ children }: { children: ReactNode }) => {
  const { currentAccount } = useAuth();
  const location = useLocation();
  const { performance } = useGdprTracking();
  const isTvSource = useTvSource();
  const isEmbedRoute = isEmbedPath(location.pathname);
  const token = appConfig.MIXPANEL_TOKEN;

  const track: typeof mixpanel.track = (...args) => {
    // Skip tracking for T+ sourced sessions
    if (isTvSource) return;
    if (isEmbedRoute) return;
    if (!performance) return;

    if (isMixpanelInitialized()) {
      mixpanel.track(...args);
    } else {
      console.info("Track", args);
    }
  };

  const optInTracking = () => {
    if (isTvSource) return;
    if (isEmbedRoute) return;
    if (!performance) return;
    if (!token) return;

    initMixpanel(token);
    if (!isMixpanelInitialized()) return;

    if (mixpanel.has_opted_out_tracking()) {
      mixpanel.opt_in_tracking();
    }
  };

  const optOutTracking = () => {
    if (isTvSource) return;
    if (isEmbedRoute) return;
    if (!isMixpanelInitialized()) return;

    mixpanel.opt_out_tracking();
  };

  useEffect(() => {
    // Skip Mixpanel initialization for T+ sourced sessions
    if (isTvSource) return;
    if (isEmbedRoute) return;
    if (!token) return;

    if (performance) {
      initMixpanel(token);
      if (!isMixpanelInitialized()) return;

      mixpanel.opt_in_tracking();

      if (currentAccount?.id) {
        mixpanel.identify(currentAccount.username);
        mixpanel.people.set({
          $email: currentAccount.source!.email,
          $name: currentAccount.display_name,
          $avatar: currentAccount.avatar_static,
          $created: currentAccount.created_at,
          Username: currentAccount.username,
          "Build Version": sourceCode.version,
          "Max Touch Points": navigator.maxTouchPoints,
        });
      }
    } else if (isMixpanelInitialized()) {
      mixpanel.opt_out_tracking();
    }
  }, [currentAccount?.id, token, performance, isTvSource, isEmbedRoute]);

  return (
    <AnalyticsContext.Provider value={{ track, optInTracking, optOutTracking }}>
      {children}
    </AnalyticsContext.Provider>
  );
};

function usePageTracking() {
  const location = useLocation();
  const { track } = useAnalytics();

  useEffect(() => {
    track("PageView", {
      Path: location.pathname,
      Search: location.search,
    });
  }, [location.pathname]);
}

export { useAnalytics, usePageTracking, AnalyticsContext };
export default AnalyticsProvider;
